<template>
  <section class="how-it-works">
    <h2>How It Works</h2>
    <p class="intro">No pressure and no guesswork. Here's what happens after you reach out.</p>
    <ol class="steps">
      <li v-for="(step, index) in steps" :key="step.title" class="step">
        <span class="step-number" aria-hidden="true">{{ index + 1 }}</span>
        <h3>{{ step.title }}</h3>
        <p>{{ step.text }}</p>
      </li>
    </ol>
    <div v-if="bothOffices" class="step-calls">
      <a href="tel:+12692625055" class="step-call"><svg class="phone-icon" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false"><path fill="currentColor" d="M6.6 10.8a15.1 15.1 0 0 0 6.6 6.6l2.2-2.2a1 1 0 0 1 1-.25 11.4 11.4 0 0 0 3.6.57 1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A17 17 0 0 1 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1c0 1.25.2 2.45.57 3.57a1 1 0 0 1-.25 1z"/></svg>Niles: (269) 262-5055</a>
      <a href="tel:+19898583559" class="step-call"><svg class="phone-icon" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false"><path fill="currentColor" d="M6.6 10.8a15.1 15.1 0 0 0 6.6 6.6l2.2-2.2a1 1 0 0 1 1-.25 11.4 11.4 0 0 0 3.6.57 1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A17 17 0 0 1 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1c0 1.25.2 2.45.57 3.57a1 1 0 0 1-.25 1z"/></svg>Gaylord: (989) 858-3559</a>
    </div>
    <a v-else :href="'tel:' + phoneHref" class="step-call"><svg class="phone-icon" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false"><path fill="currentColor" d="M6.6 10.8a15.1 15.1 0 0 0 6.6 6.6l2.2-2.2a1 1 0 0 1 1-.25 11.4 11.4 0 0 0 3.6.57 1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A17 17 0 0 1 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1c0 1.25.2 2.45.57 3.57a1 1 0 0 1-.25 1z"/></svg>Call {{ phone }} to get started</a>
  </section>
</template>

<script>
export default {
  name: 'HowItWorks',
  props: {
    phone: { type: String, default: '(269) 262-5055' },
    phoneHref: { type: String, default: '+12692625055' },
    // Pages that serve both locations show both office numbers
    bothOffices: { type: Boolean, default: false }
  },
  computed: {
    steps() {
      return [
        {
          title: 'Call or request a quote',
          text: this.bothOffices
            ? 'Call the office closest to you or send the free quote form. Tell us what\'s going on with your roof.'
            : `Call ${this.phone} or send the free quote form. Tell us what's going on with your roof.`
        },
        {
          title: 'Free inspection and estimate',
          text: 'We look over your roof and give you a clear quote. It costs nothing and you are never obligated.'
        },
        {
          title: 'We do the work',
          text: 'Our licensed, insured crew does the job with quality materials and cleans up the site when we finish.'
        },
        {
          title: 'Protected for years',
          text: 'Ask about our 50-year warranty. We will explain exactly what it covers for your roof.'
        }
      ];
    }
  }
};
</script>

<style scoped>
.how-it-works {
  max-width: 1080px;
  margin: 0 auto;
  padding: 30px;
  text-align: center;
}

h2 {
  font-size: clamp(1.35rem, 2.5vw + 0.5rem, 2.2rem);
  color: #f1c40f;
  margin: 0 0 12px;
  padding-bottom: 10px;
  border-bottom: 3px solid #f1c40f;
  display: inline-block;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
}

.intro {
  margin: 0 auto 26px;
  color: #e8e8e8;
  font-size: clamp(0.95rem, 1vw + 0.5rem, 1.12rem);
  line-height: 1.7;
}

.steps {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 18px;
  counter-reset: none;
}

.step {
  position: relative;
  background: rgba(255, 255, 255, 0.06);
  border-top: 4px solid #f1c40f;
  border-radius: 10px;
  padding: 26px 20px 22px;
  text-align: left;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.25);
}

.step-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #f1c40f;
  color: #1a1a1a;
  font-weight: 900;
  font-size: 1.15rem;
  margin-bottom: 14px;
}

h3 {
  margin: 0 0 8px;
  color: #fff;
  font-size: clamp(1.02rem, 1vw + 0.6rem, 1.18rem);
  line-height: 1.35;
}

p {
  margin: 0;
  color: #e0e0e0;
  font-size: 0.98rem;
  line-height: 1.6;
}

.step-call {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-radius: 10px;
  background: linear-gradient(135deg, #8d0808 0%, #bf1414 55%, #e04a2d 100%);
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.34), 0 0 0 1px rgba(255, 255, 255, 0.08) inset;
  color: #fff;
  font-weight: 600;
  letter-spacing: 0.3px;
  text-decoration: none;
  cursor: pointer;
  transition: transform 0.22s ease, box-shadow 0.22s ease, filter 0.22s ease;
  margin-top: 26px;
  padding: 14px 28px;
  font-size: 1.02rem;
}

.step-call:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.32), 0 0 0 1px rgba(255, 255, 255, 0.1) inset;
  filter: brightness(1.05);
}

.step-call:active {
  transform: translateY(1px) scale(0.995);
  filter: brightness(0.98);
}

.step-call:focus-visible {
  outline: 3px solid #f1c40f;
  outline-offset: 2px;
}

.step-calls {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
}

@media (max-width: 900px) {
  .steps {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 560px) {
  .how-it-works {
    padding: 24px 16px;
  }

  .steps {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .step {
    display: grid;
    grid-template-columns: 40px 1fr;
    column-gap: 14px;
    padding: 18px 16px;
  }

  .step-number {
    grid-row: span 2;
    margin: 0;
  }
}
</style>
